import { DOC_PERMISSION_RANK, type DocFolderWithAccess } from "@estelle/domain";
import type { WriteRejection } from "@estelle/domain";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { FolderTree } from "@/modules/docs/screens/FolderTree";
import { RejectionNotice } from "@/modules/docs/screens/RejectionNotice";

/**
 * The folder picker behind "Move to…" (FR-DOC-10, #213).
 *
 * Only offers a folder the caller holds at least Write on — a courtesy, not
 * the enforcement: the API refuses the move itself regardless of what this
 * list shows (`movePage`'s own write-on-both-ends check). Filtering it here
 * just spares picking a destination that would only ever come back
 * `forbidden`.
 *
 * Fully controlled, the same reason `NamePromptDialog` is: the caller decides
 * which page is moving and resets `selectedFolderId` to `null` in the same
 * click that opens this, rather than an internal draft this component would
 * need an effect to clear between one page's move and the next.
 */
export function MoveToFolderDialog({
  open,
  onOpenChange,
  folders,
  currentFolderId,
  selectedFolderId,
  onChangeSelectedFolderId,
  pending,
  rejection,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folders: readonly DocFolderWithAccess[];
  /** Excluded from the picker — moving a page into the folder it is already in is not a move. */
  currentFolderId: string;
  selectedFolderId: string | null;
  onChangeSelectedFolderId: (folderId: string) => void;
  pending: boolean;
  rejection: WriteRejection | null;
  onSubmit: (folderId: string) => void;
}) {
  const writableFolders = folders.filter(
    (folder) =>
      folder.id !== currentFolderId &&
      DOC_PERMISSION_RANK[folder.effectiveLevel] >= DOC_PERMISSION_RANK.write,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="Move to…">
        <div className="space-y-3">
          {writableFolders.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              There is nowhere else you can write to move this into.
            </p>
          ) : (
            <div className="max-h-72 overflow-auto rounded-md border p-2">
              <FolderTree
                folders={writableFolders}
                selectedFolderId={selectedFolderId}
                onSelectFolder={onChangeSelectedFolderId}
              />
            </div>
          )}

          {rejection ? <RejectionNotice rejection={rejection} /> : null}

          <div className="flex justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={selectedFolderId === null || pending}
              onClick={() => {
                if (selectedFolderId !== null) onSubmit(selectedFolderId);
              }}
            >
              Move
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
